import type { SqlDataset } from "@bezpart-board/shared";

export type SqlTask = {
  /** Стабильный id — по нему запоминаются решённые задания. */
  id: string;
  /**
   * Тема в виде «номер темы + название»: номер совпадает с главой учебника
   * sql-academy.org/ru/guide, чтобы задание можно было делать сразу после урока.
   * 2.x — «Базовая выборка. Часть 1», 3.x — «Часть 2», 4.x — «Изменение данных».
   */
  topic: string;
  prompt: string;
  /**
   * Эталонный запрос. Он не сравнивается с текстом ответа: перед проверкой
   * его выполняют на чистой копии базы и сравнивают ПОЛУЧЕННЫЕ ДАННЫЕ
   * с данными ученика (см. compareResults в sqlRunner.ts).
   */
  solution: string;
  hint?: string;
};

/*
 * Нумерация тем по учебнику sql-academy:
 *   2.1  Базовый синтаксис SQL-запроса      3.1  Многотабличный запрос
 *   2.2  Литералы                           3.2  INNER JOIN
 *   2.3  Использование функций              3.3  OUTER JOIN
 *   2.4  DISTINCT                           3.4  LIMIT
 *   2.5  WHERE                              3.5  Вложенные запросы
 *   2.6  IS NULL, BETWEEN, IN               3.6  Подзапрос: одна строка
 *   2.7  LIKE                               3.7  Подзапрос: много строк
 *   2.8  REGEXP (здесь нет: SQLite без него) 3.9 Коррелированный подзапрос
 *   2.9  Сортировка                         3.10 WITH
 *   2.10 Группировка                        3.11 Объединение запросов
 *   2.11 Агрегатные функции                 3.12 CASE
 *   2.12 HAVING                             4.1  INSERT, 4.2 UPDATE, 4.3 DELETE
 *
 * Условие всегда перечисляет нужные столбцы: проверка сравнивает данные,
 * поэтому лишний или недостающий столбец засчитать нельзя.
 */

const AIRBNB_TASKS: SqlTask[] = [
  {
    id: "airbnb-01",
    topic: "2.1 SELECT",
    prompt: "Выведите все столбцы таблицы Rooms — полный список жилья.",
    solution: "SELECT * FROM Rooms",
    hint: "Звёздочка * означает «все столбцы».",
  },
  {
    id: "airbnb-02",
    topic: "2.1 SELECT",
    prompt: "Выведите только номер, тип жилья и цену (id, home_type, price).",
    solution: "SELECT id, home_type, price FROM Rooms",
    hint: "Вместо звёздочки перечислите нужные столбцы через запятую.",
  },
  {
    id: "airbnb-03",
    topic: "2.1 Псевдонимы",
    prompt:
      "Выведите адрес и стоимость недели проживания (address, price * 7). Второй столбец назовите week_price.",
    solution: "SELECT address, price * 7 AS week_price FROM Rooms",
    hint: "Арифметику можно писать прямо в SELECT, а имя столбцу дать через AS.",
  },
  {
    id: "airbnb-04",
    topic: "2.3 Функции",
    prompt: "Выведите имя каждого пользователя и длину имени в символах (name, LENGTH(name)).",
    solution: "SELECT name, LENGTH(name) AS len FROM Users",
    hint: "LENGTH(строка) возвращает количество символов.",
  },
  {
    id: "airbnb-05",
    topic: "2.4 DISTINCT",
    prompt: "Выведите список типов жилья (home_type) без повторов.",
    solution: "SELECT DISTINCT home_type FROM Rooms",
    hint: "DISTINCT убирает одинаковые строки результата.",
  },
  {
    id: "airbnb-06",
    topic: "2.5 WHERE",
    prompt: "Выведите адреса (address) всех комнат дороже 3000 за ночь.",
    solution: "SELECT address FROM Rooms WHERE price > 3000",
  },
  {
    id: "airbnb-07",
    topic: "2.5 WHERE",
    prompt:
      "Выведите номер и адрес (id, address) жилья, где есть одновременно и телевизор, и интернет.",
    solution: "SELECT id, address FROM Rooms WHERE has_tv = 1 AND has_internet = 1",
    hint: "Два условия соединяются словом AND. Единица здесь значит «да».",
  },
  {
    id: "airbnb-08",
    topic: "2.6 IS NULL",
    prompt:
      "Выведите имя и почту (name, email) пользователей, которые не подтвердили адрес почты.",
    solution: "SELECT name, email FROM Users WHERE email_verified_at IS NULL",
    hint: "Пустое значение проверяют через IS NULL, а не через = NULL.",
  },
  {
    id: "airbnb-09",
    topic: "2.6 BETWEEN",
    prompt: "Выведите номер и цену (id, price) жилья с ценой от 1000 до 3000 включительно.",
    solution: "SELECT id, price FROM Rooms WHERE price BETWEEN 1000 AND 3000",
  },
  {
    id: "airbnb-10",
    topic: "2.6 IN",
    prompt:
      "Выведите адрес и тип (address, home_type) жилья типа «Private room» или «Shared room». Используйте IN.",
    solution: "SELECT address, home_type FROM Rooms WHERE home_type IN ('Private room', 'Shared room')",
  },
  {
    id: "airbnb-11",
    topic: "2.7 LIKE",
    prompt: "Выведите адреса (address) жилья в Нью-Йорке — адрес заканчивается на «New York».",
    solution: "SELECT address FROM Rooms WHERE address LIKE '%New York'",
    hint: "Знак % заменяет любой кусок текста: LIKE '%New York' — «заканчивается на».",
  },
  {
    id: "airbnb-12",
    topic: "2.7 LIKE",
    prompt: "Выведите имя и почту (name, email) пользователей с почтой на mail.ru.",
    solution: "SELECT name, email FROM Users WHERE email LIKE '%mail.ru'",
  },
  {
    id: "airbnb-13",
    topic: "2.9 Сортировка",
    prompt: "Выведите адрес и цену (address, price) всего жилья, начиная с самого дорогого.",
    solution: "SELECT address, price FROM Rooms ORDER BY price DESC",
    hint: "DESC — по убыванию.",
  },
  {
    id: "airbnb-14",
    topic: "2.9 Сортировка",
    prompt:
      "Выведите тип и адрес (home_type, address) всего жилья: сначала по типу по алфавиту, внутри типа — по адресу.",
    solution: "SELECT home_type, address FROM Rooms ORDER BY home_type, address",
    hint: "В ORDER BY можно перечислить несколько столбцов через запятую.",
  },
  {
    id: "airbnb-15",
    topic: "2.10 Группировка",
    prompt:
      "Посчитайте количество объявлений каждого типа. Выведите тип и число объявлений (home_type, count).",
    solution: "SELECT home_type, COUNT(*) AS cnt FROM Rooms GROUP BY home_type",
    hint: "GROUP BY home_type собирает строки в группы, COUNT(*) считает строки внутри каждой.",
  },
  {
    id: "airbnb-16",
    topic: "2.11 Агрегатные функции",
    prompt: "Посчитайте, сколько всего объявлений в таблице Rooms. Один столбец с числом.",
    solution: "SELECT COUNT(*) FROM Rooms",
  },
  {
    id: "airbnb-17",
    topic: "2.11 Агрегатные функции",
    prompt: "Выведите минимальную, среднюю и максимальную цену жилья — три столбца в одной строке.",
    solution: "SELECT MIN(price), AVG(price), MAX(price) FROM Rooms",
  },
  {
    id: "airbnb-18",
    topic: "2.12 HAVING",
    prompt:
      "Выведите типы жилья и их среднюю цену (home_type, средняя цена) для тех типов, где средняя цена выше 3000.",
    solution:
      "SELECT home_type, AVG(price) AS avg_price FROM Rooms GROUP BY home_type HAVING AVG(price) > 3000",
    hint: "HAVING — это WHERE, но для уже сгруппированных данных.",
  },
  {
    id: "airbnb-19",
    topic: "3.2 INNER JOIN",
    prompt: "Выведите адрес жилья и имя его владельца (address, name).",
    solution: "SELECT rm.address, us.name FROM Rooms rm JOIN Users us ON us.id = rm.owner_id",
    hint: "Rooms.owner_id указывает на Users.id.",
  },
  {
    id: "airbnb-20",
    topic: "3.2 INNER JOIN",
    prompt:
      "Для каждой брони выведите имя гостя, адрес жилья и дату заезда (name, address, start_date).",
    solution:
      "SELECT us.name, rm.address, rs.start_date FROM Reservations rs JOIN Users us ON us.id = rs.user_id JOIN Rooms rm ON rm.id = rs.room_id",
    hint: "Две связи: Reservations → Users и Reservations → Rooms.",
  },
  {
    id: "airbnb-21",
    topic: "3.2 INNER JOIN",
    prompt:
      "Посчитайте средний рейтинг каждого объявления по отзывам. Выведите номер жилья и средний рейтинг (room_id, рейтинг). Жильё без отзывов не показывайте.",
    solution:
      "SELECT rs.room_id, AVG(rw.rating) AS rating FROM Reservations rs JOIN Reviews rw ON rw.reservation_id = rs.id GROUP BY rs.room_id",
    hint: "Отзыв привязан к брони, а бронь — к жилью: Reviews → Reservations → room_id.",
  },
  {
    id: "airbnb-22",
    topic: "3.3 OUTER JOIN",
    prompt: "Выведите имена (name) пользователей, которые ни разу ничего не бронировали.",
    solution:
      "SELECT us.name FROM Users us LEFT JOIN Reservations rs ON rs.user_id = us.id WHERE rs.id IS NULL",
    hint: "LEFT JOIN оставит пользователя без броней, и столбцы Reservations будут NULL.",
  },
  {
    id: "airbnb-23",
    topic: "3.4 LIMIT",
    prompt: "Выведите адрес и цену (address, price) трёх самых дорогих объявлений.",
    solution: "SELECT address, price FROM Rooms ORDER BY price DESC LIMIT 3",
  },
  {
    id: "airbnb-24",
    topic: "3.6 Подзапрос",
    prompt: "Выведите адреса (address) жилья, цена которого выше средней цены по всей базе.",
    solution: "SELECT address FROM Rooms WHERE price > (SELECT AVG(price) FROM Rooms)",
    hint: "Подзапрос в скобках возвращает одно число — с ним и сравниваем.",
  },
  {
    id: "airbnb-25",
    topic: "3.7 Подзапрос",
    prompt: "Выведите адреса (address) жилья, которое ни разу не бронировали.",
    solution: "SELECT address FROM Rooms WHERE id NOT IN (SELECT room_id FROM Reservations)",
  },
  {
    id: "airbnb-26",
    topic: "3.9 Коррелированный подзапрос",
    prompt:
      "Для каждого пользователя выведите имя и число его броней (name, число). У кого броней нет — ноль.",
    solution:
      "SELECT us.name, (SELECT COUNT(*) FROM Reservations rs WHERE rs.user_id = us.id) AS bookings FROM Users us",
    hint: "Подзапрос внутри SELECT считается заново для каждой строки внешнего запроса.",
  },
  {
    id: "airbnb-27",
    topic: "3.10 WITH",
    prompt:
      "Через WITH посчитайте выручку каждого объявления (сумма total) и выведите адрес и выручку (address, выручка) для тех, у кого она больше 20000.",
    solution:
      "WITH income AS (SELECT room_id, SUM(total) AS total_sum FROM Reservations GROUP BY room_id) SELECT rm.address, i.total_sum FROM income i JOIN Rooms rm ON rm.id = i.room_id WHERE i.total_sum > 20000",
    hint: "WITH имя AS (запрос) — временная таблица, к которой потом можно обратиться.",
  },
  {
    id: "airbnb-28",
    topic: "3.11 Объединение запросов",
    prompt:
      "Выведите одним столбцом (name) без повторов имена тех, кто сдаёт жильё, и тех, кто хоть раз бронировал.",
    solution:
      "SELECT name FROM Users WHERE id IN (SELECT owner_id FROM Rooms) UNION SELECT name FROM Users WHERE id IN (SELECT user_id FROM Reservations)",
    hint: "UNION склеивает результаты двух запросов и убирает повторы.",
  },
  {
    id: "airbnb-29",
    topic: "3.12 CASE",
    prompt:
      "Выведите адрес и метку цены (address, метка): «дорого», если цена 3000 и выше, иначе «дёшево».",
    solution:
      "SELECT address, CASE WHEN price >= 3000 THEN 'дорого' ELSE 'дёшево' END AS label FROM Rooms",
  },
  {
    id: "airbnb-30",
    topic: "4.1 INSERT",
    prompt:
      "Добавьте пользователя: id 13, name «Alan Rickman», email «rickman@gmail.com», почта не подтверждена (NULL), password «q1w2e3r4t5y6», phone_number «+44 20 7946 0999». Затем выведите id, name, email всех пользователей с неподтверждённой почтой по возрастанию id. Обе команды пишите одну за другой через точку с запятой.",
    solution:
      "INSERT INTO Users (id, name, email, email_verified_at, password, phone_number) VALUES (13, 'Alan Rickman', 'rickman@gmail.com', NULL, 'q1w2e3r4t5y6', '+44 20 7946 0999');\nSELECT id, name, email FROM Users WHERE email_verified_at IS NULL ORDER BY id",
    hint: "База пересоздаётся перед каждым запуском, поэтому вставку можно повторять сколько угодно.",
  },
  {
    id: "airbnb-31",
    topic: "4.2 UPDATE",
    prompt:
      "Поднимите цену на 10% у всего жилья с кондиционером (has_air_con = 1), затем выведите id и price всего жилья по возрастанию id.",
    solution:
      "UPDATE Rooms SET price = price * 1.1 WHERE has_air_con = 1;\nSELECT id, price FROM Rooms ORDER BY id",
    hint: "Без WHERE обновились бы все строки — это частая ошибка.",
  },
  {
    id: "airbnb-32",
    topic: "4.3 DELETE",
    prompt:
      "Удалите отзывы с рейтингом меньше 3, затем выведите все столбцы оставшихся отзывов по возрастанию id.",
    solution: "DELETE FROM Reviews WHERE rating < 3;\nSELECT * FROM Reviews ORDER BY id",
  },
];

const FAMILY_TASKS: SqlTask[] = [
  {
    id: "family-01",
    topic: "2.1 SELECT",
    prompt: "Выведите все столбцы таблицы FamilyMembers.",
    solution: "SELECT * FROM FamilyMembers",
  },
  {
    id: "family-02",
    topic: "2.1 SELECT",
    prompt: "Выведите номер и имя каждого члена семьи (member_id, member_name).",
    solution: "SELECT member_id, member_name FROM FamilyMembers",
  },
  {
    id: "family-03",
    topic: "2.1 Псевдонимы",
    prompt:
      "Для каждого платежа выведите его номер и полную стоимость (payment_id, unit_price * amount). Второй столбец назовите price.",
    solution: "SELECT payment_id, unit_price * amount AS price FROM Payments",
    hint: "Полная стоимость — цена за штуку, умноженная на количество.",
  },
  {
    id: "family-04",
    topic: "2.3 Функции",
    prompt: "Выведите имя и год рождения каждого члена семьи (member_name, год).",
    solution: "SELECT member_name, YEAR(birthday) AS birth_year FROM FamilyMembers",
    hint: "YEAR(дата) вытаскивает из даты год.",
  },
  {
    id: "family-05",
    topic: "2.4 DISTINCT",
    prompt: "Выведите список ролей в семье (status) без повторов.",
    solution: "SELECT DISTINCT status FROM FamilyMembers",
  },
  {
    id: "family-06",
    topic: "2.5 WHERE",
    prompt: "Выведите имена (member_name) всех мам — status равен «mother».",
    solution: "SELECT member_name FROM FamilyMembers WHERE status = 'mother'",
  },
  {
    id: "family-07",
    topic: "2.5 WHERE",
    prompt: "Выведите названия товаров (good_name) с типом 1.",
    solution: "SELECT good_name FROM Goods WHERE type = 1",
  },
  {
    id: "family-08",
    topic: "2.6 BETWEEN",
    prompt:
      "Выведите номер платежа и цену за штуку (payment_id, unit_price) для платежей с ценой от 100 до 500 включительно.",
    solution: "SELECT payment_id, unit_price FROM Payments WHERE unit_price BETWEEN 100 AND 500",
  },
  {
    id: "family-09",
    topic: "2.6 IN",
    prompt: "Выведите имена (member_name) родителей — тех, у кого status «father» или «mother». Используйте IN.",
    solution: "SELECT member_name FROM FamilyMembers WHERE status IN ('father', 'mother')",
  },
  {
    id: "family-10",
    topic: "2.7 LIKE",
    prompt: "Выведите имена (member_name) всех членов семьи с фамилией Quincey.",
    solution: "SELECT member_name FROM FamilyMembers WHERE member_name LIKE '%Quincey'",
  },
  {
    id: "family-11",
    topic: "2.7 LIKE",
    prompt: "Выведите названия товаров (good_name), которые заканчиваются на «caviar».",
    solution: "SELECT good_name FROM Goods WHERE good_name LIKE '%caviar'",
  },
  {
    id: "family-12",
    topic: "2.9 Сортировка",
    prompt: "Выведите имя и дату рождения (member_name, birthday), начиная с самого старшего.",
    solution: "SELECT member_name, birthday FROM FamilyMembers ORDER BY birthday",
  },
  {
    id: "family-13",
    topic: "2.9 Сортировка",
    prompt:
      "Выведите название товара и его тип (good_name, type): сначала по типу по возрастанию, внутри типа — по названию по алфавиту.",
    solution: "SELECT good_name, type FROM Goods ORDER BY type, good_name",
  },
  {
    id: "family-14",
    topic: "2.10 Группировка",
    prompt:
      "Посчитайте, сколько платежей сделал каждый член семьи. Выведите его номер и число платежей (family_member, число).",
    solution: "SELECT family_member, COUNT(*) AS cnt FROM Payments GROUP BY family_member",
  },
  {
    id: "family-15",
    topic: "2.11 Агрегатные функции",
    prompt: "Посчитайте, сколько всего платежей в таблице Payments. Один столбец с числом.",
    solution: "SELECT COUNT(*) FROM Payments",
  },
  {
    id: "family-16",
    topic: "2.11 Агрегатные функции",
    prompt: "Посчитайте, сколько семья потратила всего (сумма цена × количество). Один столбец.",
    solution: "SELECT SUM(unit_price * amount) FROM Payments",
  },
  {
    id: "family-17",
    topic: "2.11 Агрегатные функции",
    prompt: "Посчитайте среднюю цену за штуку по всем платежам. Один столбец.",
    solution: "SELECT AVG(unit_price) FROM Payments",
  },
  {
    id: "family-18",
    topic: "2.12 HAVING",
    prompt:
      "Выведите номер члена семьи и его траты (family_member, сумма цена × количество) для тех, кто потратил больше 5000.",
    solution:
      "SELECT family_member, SUM(unit_price * amount) AS total FROM Payments GROUP BY family_member HAVING SUM(unit_price * amount) > 5000",
  },
  {
    id: "family-19",
    topic: "3.1 Многотабличный запрос",
    prompt: "Выведите название товара и название его категории (good_name, good_type_name).",
    solution:
      "SELECT g.good_name, gt.good_type_name FROM Goods g JOIN GoodTypes gt ON gt.good_type_id = g.type",
    hint: "Goods.type указывает на GoodTypes.good_type_id.",
  },
  {
    id: "family-20",
    topic: "3.2 INNER JOIN",
    prompt: "Для каждого платежа выведите, кто и что купил (member_name, good_name).",
    solution:
      "SELECT fm.member_name, g.good_name FROM Payments p JOIN FamilyMembers fm ON fm.member_id = p.family_member JOIN Goods g ON g.good_id = p.good",
    hint: "Payments.family_member → FamilyMembers.member_id, Payments.good → Goods.good_id.",
  },
  {
    id: "family-21",
    topic: "3.2 INNER JOIN",
    prompt:
      "Найдите среднюю цену за штуку для икры — в базе есть «red caviar» и «black caviar». Один столбец.",
    solution:
      "SELECT AVG(p.unit_price) FROM Payments p JOIN Goods g ON g.good_id = p.good WHERE g.good_name IN ('red caviar', 'black caviar')",
  },
  {
    id: "family-22",
    topic: "3.3 OUTER JOIN",
    prompt: "Выведите имена (member_name) членов семьи, которые ничего не покупали.",
    solution:
      "SELECT fm.member_name FROM FamilyMembers fm LEFT JOIN Payments p ON p.family_member = fm.member_id WHERE p.payment_id IS NULL",
  },
  {
    id: "family-23",
    topic: "3.4 LIMIT",
    prompt:
      "Выведите название товара и полную стоимость (good_name, unit_price * amount) для трёх самых дорогих покупок.",
    solution:
      "SELECT g.good_name, p.unit_price * p.amount AS price FROM Payments p JOIN Goods g ON g.good_id = p.good ORDER BY price DESC LIMIT 3",
  },
  {
    id: "family-24",
    topic: "3.6 Подзапрос",
    prompt: "Выведите номера платежей (payment_id) с самой большой ценой за штуку.",
    solution: "SELECT payment_id FROM Payments WHERE unit_price = (SELECT MAX(unit_price) FROM Payments)",
  },
  {
    id: "family-25",
    topic: "3.7 Подзапрос",
    prompt: "Выведите названия товаров (good_name), которые ни разу не покупали.",
    solution: "SELECT good_name FROM Goods WHERE good_id NOT IN (SELECT good FROM Payments)",
  },
  {
    id: "family-26",
    topic: "3.9 Коррелированный подзапрос",
    prompt:
      "Для каждого члена семьи выведите имя и сумму его трат (member_name, сумма). У кого покупок нет — NULL.",
    solution:
      "SELECT fm.member_name, (SELECT SUM(p.unit_price * p.amount) FROM Payments p WHERE p.family_member = fm.member_id) AS spent FROM FamilyMembers fm",
  },
  {
    id: "family-27",
    topic: "3.10 WITH",
    prompt:
      "Через WITH посчитайте траты каждого члена семьи и выведите имя и сумму (member_name, сумма) для тех, кто потратил больше 5000.",
    solution:
      "WITH spend AS (SELECT family_member, SUM(unit_price * amount) AS total FROM Payments GROUP BY family_member) SELECT fm.member_name, s.total FROM spend s JOIN FamilyMembers fm ON fm.member_id = s.family_member WHERE s.total > 5000",
  },
  {
    id: "family-28",
    topic: "3.11 Объединение запросов",
    prompt:
      "Выведите одним столбцом (good_name) названия всех товаров категории «food» и всех товаров категории «delicacies».",
    solution:
      "SELECT good_name FROM Goods WHERE type = (SELECT good_type_id FROM GoodTypes WHERE good_type_name = 'food') UNION SELECT good_name FROM Goods WHERE type = (SELECT good_type_id FROM GoodTypes WHERE good_type_name = 'delicacies')",
  },
  {
    id: "family-29",
    topic: "3.12 CASE",
    prompt:
      "Выведите имя и роль в семье (member_name, метка): «взрослый» для father и mother, «ребёнок» для всех остальных.",
    solution:
      "SELECT member_name, CASE WHEN status IN ('father', 'mother') THEN 'взрослый' ELSE 'ребёнок' END AS kind FROM FamilyMembers",
  },
  {
    id: "family-30",
    topic: "4.1 INSERT",
    prompt:
      "Добавьте в Goods товар: good_id 21, good_name «Cheese», тип — категория «food» (номер возьмите подзапросом из GoodTypes). Затем выведите все столбцы Goods по возрастанию good_id.",
    solution:
      "INSERT INTO Goods (good_id, good_name, type) VALUES (21, 'Cheese', (SELECT good_type_id FROM GoodTypes WHERE good_type_name = 'food'));\nSELECT * FROM Goods ORDER BY good_id",
  },
  {
    id: "family-31",
    topic: "4.2 UPDATE",
    prompt:
      "Удвойте цену за штуку во всех платежах за хлеб («bread»), затем выведите payment_id и unit_price всех платежей по возрастанию payment_id.",
    solution:
      "UPDATE Payments SET unit_price = unit_price * 2 WHERE good = (SELECT good_id FROM Goods WHERE good_name = 'bread');\nSELECT payment_id, unit_price FROM Payments ORDER BY payment_id",
  },
  {
    id: "family-32",
    topic: "4.3 DELETE",
    prompt:
      "Удалите все платежи дочерей (status «daughter»), затем выведите payment_id и family_member оставшихся платежей по возрастанию payment_id.",
    solution:
      "DELETE FROM Payments WHERE family_member IN (SELECT member_id FROM FamilyMembers WHERE status = 'daughter');\nSELECT payment_id, family_member FROM Payments ORDER BY payment_id",
  },
];

const AIRPORT_TASKS: SqlTask[] = [
  {
    id: "airport-01",
    topic: "2.1 SELECT",
    prompt: "Выведите все столбцы таблицы Company — список авиакомпаний.",
    solution: "SELECT * FROM Company",
  },
  {
    id: "airport-02",
    topic: "2.1 SELECT",
    prompt: "Выведите имена (name) всех пассажиров базы.",
    solution: "SELECT name FROM Passenger",
  },
  {
    id: "airport-03",
    topic: "2.1 SELECT",
    prompt: "Выведите город вылета, город прилёта и модель самолёта (town_from, town_to, plane) всех рейсов.",
    solution: "SELECT town_from, town_to, plane FROM Trip",
  },
  {
    id: "airport-04",
    topic: "2.3 Функции",
    prompt: "Выведите номер рейса и час вылета (id, час из time_out).",
    solution: "SELECT id, HOUR(time_out) AS hour_out FROM Trip",
    hint: "HOUR(время) достаёт из времени часы.",
  },
  {
    id: "airport-05",
    topic: "2.3 Функции",
    prompt:
      "Выведите номер рейса и время в пути (id, TIMEDIFF от времени прилёта до времени вылета) для рейсов из Парижа.",
    solution: "SELECT id, TIMEDIFF(time_in, time_out) AS flight_time FROM Trip WHERE town_from = 'Paris'",
    hint: "TIMEDIFF(время1, время2) возвращает разницу в виде «ЧЧ:ММ:СС».",
  },
  {
    id: "airport-06",
    topic: "2.4 DISTINCT",
    prompt: "Выведите модели самолётов (plane) без повторов.",
    solution: "SELECT DISTINCT plane FROM Trip",
  },
  {
    id: "airport-07",
    topic: "2.5 WHERE",
    prompt: "Выведите все столбцы рейсов, вылетающих из Москвы (town_from = 'Moscow').",
    solution: "SELECT * FROM Trip WHERE town_from = 'Moscow'",
  },
  {
    id: "airport-08",
    topic: "2.5 WHERE",
    prompt:
      "Выведите номер рейса и город прилёта (id, town_to) для рейсов на Boeing, вылетающих из Лондона.",
    solution: "SELECT id, town_to FROM Trip WHERE plane = 'Boeing' AND town_from = 'London'",
  },
  {
    id: "airport-09",
    topic: "2.5 WHERE",
    prompt:
      "Выведите номера рейсов (id), которые либо прилетают в Париж, либо выполняются на Airbus.",
    solution: "SELECT id FROM Trip WHERE town_to = 'Paris' OR plane = 'Airbus'",
    hint: "OR — достаточно выполнения одного из условий.",
  },
  {
    id: "airport-10",
    topic: "2.6 IN",
    prompt:
      "Выведите номер рейса и город вылета (id, town_from) для рейсов из Москвы или Ростова. Используйте IN.",
    solution: "SELECT id, town_from FROM Trip WHERE town_from IN ('Moscow', 'Rostov')",
  },
  {
    id: "airport-11",
    topic: "2.7 LIKE",
    prompt: "Выведите имена (name) пассажиров, чьё имя заканчивается на «man».",
    solution: "SELECT name FROM Passenger WHERE name LIKE '%man'",
  },
  {
    id: "airport-12",
    topic: "2.7 LIKE",
    prompt: "Выведите имена (name) пассажиров, начинающиеся на букву S.",
    solution: "SELECT name FROM Passenger WHERE name LIKE 'S%'",
  },
  {
    id: "airport-13",
    topic: "2.9 Сортировка",
    prompt: "Выведите номер рейса и время вылета (id, time_out), начиная с самых ранних.",
    solution: "SELECT id, time_out FROM Trip ORDER BY time_out",
  },
  {
    id: "airport-14",
    topic: "2.9 Сортировка",
    prompt: "Выведите имена пассажиров (name) в обратном алфавитном порядке.",
    solution: "SELECT name FROM Passenger ORDER BY name DESC",
  },
  {
    id: "airport-15",
    topic: "2.10 Группировка",
    prompt:
      "Посчитайте количество рейсов по каждой модели самолёта. Выведите модель и число рейсов (plane, число).",
    solution: "SELECT plane, COUNT(*) AS cnt FROM Trip GROUP BY plane",
  },
  {
    id: "airport-16",
    topic: "2.10 Группировка",
    prompt:
      "Выведите нагруженность каждого рейса: номер рейса и число пассажиров (trip, число), начиная с самых загруженных.",
    solution: "SELECT trip, COUNT(passenger) AS cnt FROM Pass_in_trip GROUP BY trip ORDER BY cnt DESC",
  },
  {
    id: "airport-17",
    topic: "2.11 Агрегатные функции",
    prompt: "Посчитайте количество рейсов на TU-154. Один столбец с числом.",
    solution: "SELECT COUNT(*) FROM Trip WHERE plane = 'TU-154'",
  },
  {
    id: "airport-18",
    topic: "2.12 HAVING",
    prompt:
      "Выведите города вылета и число рейсов из них (town_from, число) для городов, откуда больше трёх рейсов.",
    solution: "SELECT town_from, COUNT(*) AS cnt FROM Trip GROUP BY town_from HAVING COUNT(*) > 3",
  },
  {
    id: "airport-19",
    topic: "3.2 INNER JOIN",
    prompt: "Выведите номер рейса и название компании, которая его выполняет (id, name).",
    solution: "SELECT t.id, c.name FROM Trip t JOIN Company c ON c.id = t.company",
    hint: "Trip.company указывает на Company.id.",
  },
  {
    id: "airport-20",
    topic: "3.2 INNER JOIN",
    prompt: "Выведите названия компаний (name), которые летают на Boeing. Без повторов.",
    solution:
      "SELECT DISTINCT c.name FROM Trip t JOIN Company c ON c.id = t.company WHERE t.plane = 'Boeing'",
  },
  {
    id: "airport-21",
    topic: "3.2 INNER JOIN",
    prompt:
      "Выведите имя пассажира, город вылета и город прилёта (name, town_from, town_to) для всех, кто летел рейсом 1123.",
    solution:
      "SELECT p.name, t.town_from, t.town_to FROM Pass_in_trip pt JOIN Passenger p ON p.id = pt.passenger JOIN Trip t ON t.id = pt.trip WHERE t.id = 1123",
  },
  {
    id: "airport-22",
    topic: "3.3 OUTER JOIN",
    prompt: "Выведите имена (name) пассажиров, которые ни разу никуда не летели.",
    solution:
      "SELECT p.name FROM Passenger p LEFT JOIN Pass_in_trip pt ON pt.passenger = p.id WHERE pt.trip IS NULL",
  },
  {
    id: "airport-23",
    topic: "3.4 LIMIT",
    prompt: "Выведите номер и время вылета (id, time_out) самого раннего рейса.",
    solution: "SELECT id, time_out FROM Trip ORDER BY time_out LIMIT 1",
  },
  {
    id: "airport-24",
    topic: "3.6 Подзапрос",
    prompt: "Выведите название компании (name), которая выполняет рейс 1123.",
    solution: "SELECT name FROM Company WHERE id = (SELECT company FROM Trip WHERE id = 1123)",
  },
  {
    id: "airport-25",
    topic: "3.7 Подзапрос",
    prompt: "Выведите имена (name) пассажиров рейса 1100, не используя JOIN.",
    solution: "SELECT name FROM Passenger WHERE id IN (SELECT passenger FROM Pass_in_trip WHERE trip = 1100)",
  },
  {
    id: "airport-26",
    topic: "3.9 Коррелированный подзапрос",
    prompt:
      "Для каждой компании выведите название и число её рейсов (name, число). У кого рейсов нет — ноль.",
    solution:
      "SELECT c.name, (SELECT COUNT(*) FROM Trip t WHERE t.company = c.id) AS trips FROM Company c",
  },
  {
    id: "airport-27",
    topic: "3.10 WITH",
    prompt:
      "Через WITH посчитайте число пассажиров на каждом рейсе и выведите номер рейса, город вылета, город прилёта и число пассажиров (id, town_from, town_to, число) для рейсов, где пассажиров не меньше трёх.",
    solution:
      "WITH load AS (SELECT trip, COUNT(*) AS cnt FROM Pass_in_trip GROUP BY trip) SELECT t.id, t.town_from, t.town_to, l.cnt FROM load l JOIN Trip t ON t.id = l.trip WHERE l.cnt >= 3",
  },
  {
    id: "airport-28",
    topic: "3.11 Объединение запросов",
    prompt:
      "Выведите одним столбцом (town) список всех городов, которые встречаются в расписании — и как города вылета, и как города прилёта. Без повторов.",
    solution: "SELECT town_from AS town FROM Trip UNION SELECT town_to FROM Trip",
  },
  {
    id: "airport-29",
    topic: "3.12 CASE",
    prompt:
      "Выведите номер рейса и метку (id, метка): «ночной», если время прилёта меньше времени вылета (рейс через полночь), иначе «дневной».",
    solution:
      "SELECT id, CASE WHEN time_in < time_out THEN 'ночной' ELSE 'дневной' END AS kind FROM Trip",
  },
  {
    id: "airport-30",
    topic: "4.1 INSERT",
    prompt:
      "Добавьте пассажира с id 17 и именем «Hugh Grant», затем выведите все столбцы таблицы Passenger по возрастанию id.",
    solution:
      "INSERT INTO Passenger (id, name) VALUES (17, 'Hugh Grant');\nSELECT * FROM Passenger ORDER BY id",
  },
  {
    id: "airport-31",
    topic: "4.2 UPDATE",
    prompt:
      "Переименуйте компанию «Dale_avia» в «Dale_airlines», затем выведите все столбцы Company по возрастанию id.",
    solution:
      "UPDATE Company SET name = 'Dale_airlines' WHERE name = 'Dale_avia';\nSELECT * FROM Company ORDER BY id",
  },
  {
    id: "airport-32",
    topic: "4.3 DELETE",
    prompt:
      "Удалите из Pass_in_trip все посадки на рейс 1100, затем выведите trip и passenger оставшихся записей, отсортировав по trip, а внутри — по passenger.",
    solution:
      "DELETE FROM Pass_in_trip WHERE trip = 1100;\nSELECT trip, passenger FROM Pass_in_trip ORDER BY trip, passenger",
  },
];

const SCHOOL_TASKS: SqlTask[] = [
  {
    id: "school-01",
    topic: "2.1 SELECT",
    prompt: "Выведите все столбцы таблицы Student — полный список учеников.",
    solution: "SELECT * FROM Student",
  },
  {
    id: "school-02",
    topic: "2.1 SELECT",
    prompt: "Выведите имя и фамилию каждого ученика (first_name, last_name).",
    solution: "SELECT first_name, last_name FROM Student",
  },
  {
    id: "school-03",
    topic: "2.1 SELECT",
    prompt: "Выведите названия всех школьных предметов (name).",
    solution: "SELECT name FROM Subject",
  },
  {
    id: "school-04",
    topic: "2.3 Функции",
    prompt: "Выведите имя, фамилию и год рождения каждого ученика (first_name, last_name, год).",
    solution: "SELECT first_name, last_name, YEAR(birthday) AS birth_year FROM Student",
  },
  {
    id: "school-05",
    topic: "2.3 Функции",
    prompt:
      "Сколько времени ученик проведёт в школе, если учится со второй по четвёртую пару? Выведите один столбец: разницу между концом последней пары и началом первой.",
    solution: "SELECT TIMEDIFF(MAX(end_pair), MIN(start_pair)) AS time FROM Timepair WHERE id BETWEEN 2 AND 4",
  },
  {
    id: "school-06",
    topic: "2.4 DISTINCT",
    prompt: "Выведите список кабинетов (classroom) из расписания без повторов.",
    solution: "SELECT DISTINCT classroom FROM Schedule",
  },
  {
    id: "school-07",
    topic: "2.5 WHERE",
    prompt: "Выведите все столбцы учеников по имени Anna.",
    solution: "SELECT * FROM Student WHERE first_name = 'Anna'",
  },
  {
    id: "school-08",
    topic: "2.5 WHERE",
    prompt: "Выведите все столбцы уроков, которые проходили в кабинете 301.",
    solution: "SELECT * FROM Schedule WHERE classroom = 301",
  },
  {
    id: "school-09",
    topic: "2.6 BETWEEN",
    prompt:
      "Выведите номер пары и время её начала (id, start_pair) для пар со второй по четвёртую включительно.",
    solution: "SELECT id, start_pair FROM Timepair WHERE id BETWEEN 2 AND 4",
  },
  {
    id: "school-10",
    topic: "2.6 IN",
    prompt: "Выведите названия предметов (name) с номерами 1, 2 и 8. Используйте IN.",
    solution: "SELECT name FROM Subject WHERE id IN (1, 2, 8)",
  },
  {
    id: "school-11",
    topic: "2.7 LIKE",
    prompt: "Посчитайте, сколько в школе десятых классов — их названия начинаются с «10 ». Один столбец.",
    solution: "SELECT COUNT(*) FROM Class WHERE name LIKE '10 %'",
  },
  {
    id: "school-12",
    topic: "2.7 LIKE",
    prompt:
      "Выведите имя, фамилию и адрес (first_name, last_name, address) учеников, живущих на улице Пушкина (Pushkina).",
    solution: "SELECT first_name, last_name, address FROM Student WHERE address LIKE '%Pushkina%'",
    hint: "Знак % с обеих сторон — «где-то внутри строки».",
  },
  {
    id: "school-13",
    topic: "2.9 Сортировка",
    prompt: "Выведите фамилию и имя (last_name, first_name) всех учеников по алфавиту фамилий.",
    solution: "SELECT last_name, first_name FROM Student ORDER BY last_name",
  },
  {
    id: "school-14",
    topic: "2.9 Сортировка",
    prompt: "Выведите фамилию и дату рождения (last_name, birthday), начиная с самых младших.",
    solution: "SELECT last_name, birthday FROM Student ORDER BY birthday DESC",
  },
  {
    id: "school-15",
    topic: "2.10 Группировка",
    prompt:
      "Посчитайте число учеников в каждом классе. Выведите номер класса и число учеников (class, число).",
    solution: "SELECT class, COUNT(*) AS cnt FROM Student_in_class GROUP BY class",
  },
  {
    id: "school-16",
    topic: "2.10 Группировка",
    prompt:
      "Посчитайте, сколько раз использовался каждый кабинет. Выведите кабинет и число уроков (classroom, число), начиная с самых загруженных.",
    solution: "SELECT classroom, COUNT(*) AS cnt FROM Schedule GROUP BY classroom ORDER BY cnt DESC",
  },
  {
    id: "school-17",
    topic: "2.11 Агрегатные функции",
    prompt: "Посчитайте, сколько всего уроков в расписании. Один столбец с числом.",
    solution: "SELECT COUNT(*) FROM Schedule",
  },
  {
    id: "school-18",
    topic: "2.12 HAVING",
    prompt:
      "Выведите даты и число уроков (date, число) для дней, в которые прошло больше пяти уроков.",
    solution: "SELECT date, COUNT(*) AS cnt FROM Schedule GROUP BY date HAVING COUNT(*) > 5",
  },
  {
    id: "school-19",
    topic: "3.2 INNER JOIN",
    prompt:
      "Выведите дату урока и название предмета (date, name) для всех уроков класса с номером 1.",
    solution:
      "SELECT s.date, sub.name FROM Schedule s JOIN Subject sub ON sub.id = s.subject WHERE s.class = 1",
  },
  {
    id: "school-20",
    topic: "3.2 INNER JOIN",
    prompt: "Выведите имя и фамилию (first_name, last_name) учеников класса «10 A».",
    solution:
      "SELECT st.first_name, st.last_name FROM Student_in_class sic JOIN Student st ON st.id = sic.student JOIN Class c ON c.id = sic.class WHERE c.name = '10 A'",
    hint: "Student_in_class связывает Student и Class.",
  },
  {
    id: "school-21",
    topic: "3.2 INNER JOIN",
    prompt:
      "Выведите названия предметов (name), которые ведёт учитель с фамилией Romashkin. Без повторов.",
    solution:
      "SELECT DISTINCT sub.name FROM Schedule s JOIN Teacher t ON t.id = s.teacher JOIN Subject sub ON sub.id = s.subject WHERE t.last_name = 'Romashkin'",
  },
  {
    id: "school-22",
    topic: "3.3 OUTER JOIN",
    prompt: "Выведите фамилии (last_name) учителей, у которых нет ни одного урока в расписании.",
    solution:
      "SELECT t.last_name FROM Teacher t LEFT JOIN Schedule s ON s.teacher = t.id WHERE s.id IS NULL",
  },
  {
    id: "school-23",
    topic: "3.4 LIMIT",
    prompt: "Выведите фамилию, имя и дату рождения (last_name, first_name, birthday) самого старшего ученика.",
    solution: "SELECT last_name, first_name, birthday FROM Student ORDER BY birthday LIMIT 1",
  },
  {
    id: "school-24",
    topic: "3.6 Подзапрос",
    prompt: "Выведите название класса (name), в котором учится ученик с номером 2.",
    solution: "SELECT name FROM Class WHERE id = (SELECT class FROM Student_in_class WHERE student = 2)",
  },
  {
    id: "school-25",
    topic: "3.7 Подзапрос",
    prompt: "Выведите названия предметов (name), которых нет в расписании.",
    solution: "SELECT name FROM Subject WHERE id NOT IN (SELECT subject FROM Schedule)",
  },
  {
    id: "school-26",
    topic: "3.9 Коррелированный подзапрос",
    prompt:
      "Для каждого класса выведите название и число учеников в нём (name, число).",
    solution:
      "SELECT c.name, (SELECT COUNT(*) FROM Student_in_class sic WHERE sic.class = c.id) AS cnt FROM Class c",
  },
  {
    id: "school-27",
    topic: "3.10 WITH",
    prompt:
      "Через WITH посчитайте число уроков у каждого учителя и выведите фамилию и число уроков (last_name, число) для тех, у кого уроков не меньше пяти.",
    solution:
      "WITH load AS (SELECT teacher, COUNT(*) AS cnt FROM Schedule GROUP BY teacher) SELECT t.last_name, l.cnt FROM load l JOIN Teacher t ON t.id = l.teacher WHERE l.cnt >= 5",
  },
  {
    id: "school-28",
    topic: "3.11 Объединение запросов",
    prompt:
      "Выведите одним столбцом (last_name) все фамилии, которые есть в школе — и учеников, и учителей. Без повторов.",
    solution: "SELECT last_name FROM Student UNION SELECT last_name FROM Teacher",
  },
  {
    id: "school-29",
    topic: "3.12 CASE",
    prompt:
      "Выведите имя, фамилию и метку (first_name, last_name, метка): «старшие» для тех, кто родился 31.12.2005 или раньше, иначе «младшие».",
    solution:
      "SELECT first_name, last_name, CASE WHEN birthday <= '2005-12-31' THEN 'старшие' ELSE 'младшие' END AS group_name FROM Student",
  },
  {
    id: "school-30",
    topic: "4.1 INSERT",
    prompt:
      "Добавьте предмет: id 9, name «Astronomy». Затем выведите все столбцы Subject по возрастанию id.",
    solution: "INSERT INTO Subject (id, name) VALUES (9, 'Astronomy');\nSELECT * FROM Subject ORDER BY id",
  },
  {
    id: "school-31",
    topic: "4.2 UPDATE",
    prompt:
      "Исправьте фамилию учителя «Krauze» на «Kraus», затем выведите id и last_name всех учителей по возрастанию id.",
    solution:
      "UPDATE Teacher SET last_name = 'Kraus' WHERE last_name = 'Krauze';\nSELECT id, last_name FROM Teacher ORDER BY id",
  },
  {
    id: "school-32",
    topic: "4.3 DELETE",
    prompt:
      "Удалите из расписания все уроки за 2019-09-06, затем выведите id и date оставшихся уроков по возрастанию id.",
    solution:
      "DELETE FROM Schedule WHERE date = '2019-09-06';\nSELECT id, date FROM Schedule ORDER BY id",
  },
];

const TASKS_BY_DATASET: Record<SqlDataset, SqlTask[]> = {
  airbnb: AIRBNB_TASKS,
  family: FAMILY_TASKS,
  airport: AIRPORT_TASKS,
  school: SCHOOL_TASKS,
  custom: [],
};

export function tasksForDataset(dataset: SqlDataset): SqlTask[] {
  return TASKS_BY_DATASET[dataset] ?? [];
}

export function taskAt(dataset: SqlDataset, index: number): SqlTask | null {
  const list = tasksForDataset(dataset);
  if (!list.length) return null;
  const i = Math.min(Math.max(0, Math.round(index)), list.length - 1);
  return list[i];
}
